import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { ApiResponse, PaginatedResponse, PaginationMeta } from '@klaro7/shared-types';
import type { ProfessionalEarningsSummary, Payout, ConvertedUserRow } from '@klaro7/shared-types';

import api from '@/lib/api';
import { compactParams, type SignedReceiptStatusValue } from '@/features/fiscal/api';

// ── query keys ──────────────────────────────────────────────────────────────
export const payoutsQueryKeys = {
  summary: ['payouts', 'admin', 'summary'] as const,
  mine: ['payouts', 'me', 'summary'] as const,
  myConversions: ['payouts', 'me', 'conversions'] as const,
  adminList: (filters: AdminPayoutFilters) => ['payouts', 'admin', 'list', filters] as const,
};

/** Filtri dell'elenco compensi admin: tutti combinabili, '' = filtro assente. */
export interface AdminPayoutFilters {
  status?: string;
  q?: string;
  professionalId?: string;
  documentType?: string;
  receiptStatus?: string;
  from?: string;
  to?: string;
  minAmount?: string;
  maxAmount?: string;
  page?: number;
  limit?: number;
}

export interface AdminPayoutListPage {
  items: AdminPayoutListItem[];
  pagination: PaginationMeta | null;
}

export interface AdminPayoutListItem {
  id: string;
  professionalId: string;
  professionalName: string;
  professionalEmail?: string | null;
  hasReceipt?: boolean;
  hasProforma?: boolean;
  signedReceipt?: { originalName: string; uploadedAt: string; verifiedAt: string | null } | null;
  receiptStatus?: SignedReceiptStatusValue | null;
  periodFrom: string;
  periodTo: string;
  totalGross: number;
  ritenuta: number;
  netto: number;
  cassaProfessionale: number;
  documentType: 'proforma' | 'ritenuta';
  receiptNumber: number | null;
  proformaNumber: number | null;
  invoice: { originalName: string; uploadedAt: string } | null;
  confirmedAt: string | null;
  status: string;
  paidAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  createdAt: string;
}

// ── service functions ────────────────────────────────────────────────────────
async function fetchMySummary(): Promise<ProfessionalEarningsSummary> {
  const response = await api.get<ApiResponse<ProfessionalEarningsSummary>>(
    '/payouts/me/summary'
  );
  return response.data.data;
}

async function fetchMyConversions(): Promise<ConvertedUserRow[]> {
  const response = await api.get<ApiResponse<ConvertedUserRow[]>>(
    '/payouts/me/conversions'
  );
  return response.data.data;
}

async function fetchEarningsSummary(): Promise<ProfessionalEarningsSummary[]> {
  const response = await api.get<ApiResponse<ProfessionalEarningsSummary[]>>(
    '/payouts/admin/summary'
  );
  return response.data.data;
}

async function createPayoutRun(professionalId: string): Promise<Payout> {
  const response = await api.post<ApiResponse<Payout>>(
    `/payouts/admin/run/${professionalId}`
  );
  return response.data.data;
}

async function markPayoutPaid(
  payoutId: string,
  paymentReference: string
): Promise<Payout> {
  const response = await api.post<ApiResponse<Payout>>(
    `/payouts/admin/${payoutId}/mark-paid`,
    { paymentReference }
  );
  return response.data.data;
}

/** Annullamento amministrativo di una liquidazione (solo super-admin lato API). */
async function cancelPayout(payoutId: string, reason: string): Promise<Payout> {
  const response = await api.post<ApiResponse<Payout>>(
    `/payouts/admin/${payoutId}/cancel`,
    { reason }
  );
  return response.data.data;
}

async function fetchAllPayouts(filters: AdminPayoutFilters): Promise<AdminPayoutListPage> {
  const response = await api.get<PaginatedResponse<AdminPayoutListItem>>('/payouts/admin/list', {
    params: filters,
  });
  return { items: response.data.data, pagination: response.data.pagination ?? null };
}

async function verifySignedReceipt(payoutId: string, verified: boolean): Promise<Payout> {
  const response = await api.patch<ApiResponse<Payout>>(
    `/payouts/admin/${payoutId}/signed-receipt/verify`,
    { verified }
  );
  return response.data.data;
}

export type AdminPayoutDocKind = 'invoice' | 'receipt' | 'proforma' | 'signed-receipt';

/** Scarica un documento di un compenso (admin): fattura, ricevuta, proforma o ricevuta firmata. */
export async function fetchAdminPayoutDocBlob(payoutId: string, kind: AdminPayoutDocKind): Promise<Blob> {
  const res = await api.get(`/payouts/admin/${payoutId}/${kind}`, { responseType: 'blob' });
  return res.data as Blob;
}

/** Download a professional's uploaded invoice (admin). */
export async function fetchAdminInvoiceBlob(payoutId: string): Promise<Blob> {
  return fetchAdminPayoutDocBlob(payoutId, 'invoice');
}

// ── hooks ────────────────────────────────────────────────────────────────────
export function useAllPayouts(filters: AdminPayoutFilters) {
  const params = compactParams(filters);
  return useQuery({
    queryKey: payoutsQueryKeys.adminList(params),
    queryFn: () => fetchAllPayouts(params),
    placeholderData: keepPreviousData,
  });
}

/** Super-admin: segna (o toglie) la verifica della ricevuta firmata. */
export function useVerifySignedReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payoutId, verified }: { payoutId: string; verified: boolean }) =>
      verifySignedReceipt(payoutId, verified),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payouts', 'admin', 'list'] });
    },
  });
}

export function useMySummary() {
  return useQuery({
    queryKey: payoutsQueryKeys.mine,
    queryFn: fetchMySummary,
  });
}

export function useMyConversions() {
  return useQuery({
    queryKey: payoutsQueryKeys.myConversions,
    queryFn: fetchMyConversions,
  });
}

export function useEarningsSummary() {
  return useQuery({
    queryKey: payoutsQueryKeys.summary,
    queryFn: fetchEarningsSummary,
  });
}

export function useCreatePayoutRun() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (professionalId: string) => createPayoutRun(professionalId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: payoutsQueryKeys.summary });
    },
  });
}

export function useCancelPayout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ payoutId, reason }: { payoutId: string; reason: string }) =>
      cancelPayout(payoutId, reason),
    onSuccess: () => {
      // I compensi liberati tornano "in sospeso": va ricaricato anche il riepilogo.
      queryClient.invalidateQueries({ queryKey: payoutsQueryKeys.summary });
      queryClient.invalidateQueries({ queryKey: ['payouts', 'admin', 'list'] });
    },
  });
}

export function useMarkPayoutPaid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ payoutId, paymentReference }: { payoutId: string; paymentReference: string }) =>
      markPayoutPaid(payoutId, paymentReference),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: payoutsQueryKeys.summary });
      queryClient.invalidateQueries({ queryKey: ['payouts', 'admin', 'list'] });
    },
  });
}
